import moment from "moment";
import { Radio, Select, Icon, Input, InputNumber, Divider } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import RangePicker from '../../Common/DatePicker/RangePicker';
import { getSpaceAvailability } from '../HostDashboardActions';
import arrow from '../../../assets/images/common/black-arrow.svg';
import { getDatesBetween } from "../../../helper/RangePickerHelper";
import { Chip } from 'react-md';
import TimeRangePicker from '../../Common/TimePicker/TimeRangePicker';
import React, { useState, useCallback, memo, useEffect } from 'react';
import useWindowDimensions from '../../Common/Hooks/useWindowDimensions';
import SingleDatePicker from './../../Common/DatePicker/SingleDatePicker';
import { CALENDER_DATE_FORMAT, DATE_PICKER_DATE_FORMAT, IMAGES_BASE_URL } from '../../../settings';
import SeatingArrangementSelect from '../../Booking/SubComponent/SeatingArrangementSelect';
import { DAILY, BLOCK, HOURLY, WEEKLY, MONTHLY, DATE_FORMAT, DATE_FORMAT_REVERSE, TIME_FORMAT, MEET, SHOOT } from '../../Common/constants';
import { CREATE, EDIT, SEATING_ARRANGEMENT } from "../../Common/constants";
import { editReservationMergeSlot } from './../../../helper/createReservationModel';
import ReservationLoader from "../../Common/Loaders/ReservationLoader";

const CreateReservationModal = memo(({
  setVisible,
  space,
  onProceed,
  selectedSpaceDetails,
  blockType,
  previousBookingData,
  selectedDateOnCalender,
  category
}) => {

  const { width } = useWindowDimensions();
  const dispatch = useDispatch();
  const isMeet = category?.toLowerCase() === MEET || category?.toLowerCase() === SHOOT;
  const DEFAULT_DATE = new Date();
  const dayBeforeToday = new Date(DEFAULT_DATE);
  dayBeforeToday.setDate(DEFAULT_DATE.getDate() - 1);

  // --- Shared State ---
  const [selectedDate, setSelectedDate] = useState([]);
  const [duration, setDuration] = useState(1);
  const [datePickers, setDatePickers] = useState([`${moment().format('L')} - ${moment().format('L')}`]);
  const [range, setRange] = useState(duration);
  const [bookingDetails, setBookingDetails] = useState({});
  const [guestDetails, setGuestDetails] = useState({});
  const [buttonDisplayStatus, setButtonDisplayStatus] = useState(true);
  const [viewGuestCount, setViewGuestCount] = useState(1);
  const events = space?.events || [];
  const [maxGuest, setMaxGuest] = useState(
    space?.events?.[0]?.seatArrangements?.[0]?.max || 50
  );

  // --- Charging Types ---
  const [chargingTypes, setChargingTypes] = useState(space?.events?.[0]?.chargings || []);
  const [paymentType, setPaymentType] = useState(chargingTypes?.[0]?.chargingType?.name || DAILY);

  // --- Date state ---
  const [startDate, setStartDate] = useState(() => {
    if (blockType === EDIT && previousBookingData?.startingDate) {
      return isMeet
        ? moment(previousBookingData.startingDate || moment()).format(DATE_PICKER_DATE_FORMAT)
        : moment(previousBookingData.startingDate, moment.defaultFormat).format('YYYY-MM-DD');
    } else if (blockType === CREATE && selectedDateOnCalender) {
      return isMeet
        ? moment(selectedDateOnCalender).format(DATE_PICKER_DATE_FORMAT)
        : moment(selectedDateOnCalender, moment.defaultFormat).format('YYYY-MM-DD');
    }
    return isMeet ? moment().format(DATE_PICKER_DATE_FORMAT) : moment().format('YYYY-MM-DD');
  });
  const [endDate, setEndDate] = useState(moment().format(DATE_PICKER_DATE_FORMAT));
  const [startDateState, setStartDateState] = useState(
    moment(previousBookingData?.startingDate || moment()).format(DATE_PICKER_DATE_FORMAT)
  );
  const startAvailabilityDate = moment().format(DATE_FORMAT_REVERSE);
  const endAvailabilityDate = moment().add(3, 'months').format(DATE_FORMAT_REVERSE);

  // --- Meet-specific State ---
  const [slots, setSlots] = useState([]);
  const [timePickers, setTimePickers] = useState([{ beginTime: '3:00 am', endTime: '5:00 pm' }]);
  const [availableSlots, setAvailableSlots] = useState(false);
  const [selectedChips, setSelectedChips] = useState([]);
  const [seatingSVG, setSeatingSVG] = useState();
  const [seatingText, setSeatingText] = useState();
  const seatingArrangement = space?.events?.[0]?.seatArrangements || [];
  const [spaceBlockSlots, setSpaceBlockSlots] = useState([]);

  // --- Work-specific State ---
  const [calculatedEndDate, setCalculatedEndDate] = useState(null);
  const [guestCountErrorMessage, setGuestCountErrorMessage] = useState(false);
  const [unavailableDatesMessage, setUnavailableDatesMessage] = useState('');
  const [dateRangeAvailability, setDateRangeAvailability] = useState({ available: true, minGuests: 0 });

  // --- Selectors ---
  const availableDates = useSelector(state => state?.hostDashboard?.availability?.data || []);
  const selectedSpaceData = useSelector(state => state?.hostDashboard?.data?.selectedSpaceData || []);
  const isFetchingAvailableDates = useSelector(state => state?.hostDashboard?.availability?.fetching || []);

  // Meet-specific: event charging config
  const selectedEvents = selectedSpaceDetails?.events || {};
  let acceptableBookingTime = false;
  let minimumBookingPeriod = 1;
  if (selectedEvents && selectedEvents[0]?.chargings) {
    const hourlyCharging = selectedEvents[0].chargings.find((charging) => charging?.chargingType?.name === HOURLY);
    if (hourlyCharging) {
      minimumBookingPeriod = hourlyCharging.minimumBookingPeriod || 1;
      acceptableBookingTime = hourlyCharging.acceptedHalfHourBooking || false;
    }
  }

  // ===================== AVAILABILITY FETCH =====================
  useEffect(() => {
    if (space && space.events && space.events.length > 0 && paymentType) {
      dispatch(
        getSpaceAvailability(
          { spaceId: space.id, eventTypeId: space.events[0].eventType.id, startDate: startAvailabilityDate, endDate: endAvailabilityDate },
          paymentType
        )
      );
    }
  }, [space, paymentType]);

  // ===================== CHARGING TYPES INIT =====================
  useEffect(() => {
    if (!space || !space.events || !space.events.length) return;
    const newChargingTypes = space.events[0]?.chargings || [];
    setChargingTypes(newChargingTypes);
    if (blockType === EDIT && selectedSpaceData?.chargingType) {
      setPaymentType(selectedSpaceData.chargingType);
      if (isMeet) {
        setChargingTypes(newChargingTypes.filter(ct => ct?.chargingType?.name === selectedSpaceData.chargingType));
      }
    } else if (blockType === CREATE) {
      if (!paymentType || !newChargingTypes.find(ct => ct?.chargingType?.name === paymentType)) {
        setPaymentType(newChargingTypes?.[0]?.chargingType?.name);
      }
    }
  }, [space, blockType, selectedSpaceData]);

  // ===================== BLOCK SLOTS SYNC (Meet) =====================
  useEffect(() => {
    if (!isMeet) return;
    if (blockType === EDIT && selectedSpaceData?.chargingType) {
      setPaymentType(selectedSpaceData.chargingType);
    }
    if (!chargingTypes || !chargingTypes.length) return;
    const blockSlots = chargingTypes.find(charging => charging.chargingType.name === BLOCK);
    if (blockSlots) {
      const availableBlocks = blockSlots?.pricingOptions;
      setSpaceBlockSlots(availableBlocks);
    }
  }, [chargingTypes, paymentType, blockType, selectedSpaceData]);

  // ===================== PREVIOUS BOOKING DATA =====================
  useEffect(() => {
    if (previousBookingData) {
      setBookingDetails({
        title: previousBookingData?.title || "",
        description: previousBookingData?.description || ""
      });
      setGuestDetails({
        firstName: previousBookingData?.firstName || "",
        lastName: previousBookingData?.lastName || "",
        contact: previousBookingData?.contact || "",
        email: previousBookingData?.email || ""
      });
      setViewGuestCount(previousBookingData?.guestCount || 1);

      if (blockType === EDIT && previousBookingData.chargingType) {
        setPaymentType(previousBookingData.chargingType);
      }
    }

    if (isMeet) {
      // Meet-specific previous data
      setSeatingText(previousBookingData?.seatingArrangement || SEATING_ARRANGEMENT);
      let seatingArrangementSVG = seatingArrangement[0]?.seatArrangement && seatingArrangement.filter(item => {
        if (item.seatArrangement.name === previousBookingData?.seatingArrangement) return item;
      })?.[0]?.seatArrangement?.icon || "";
      blockType === EDIT ? setSeatingSVG(IMAGES_BASE_URL + seatingArrangementSVG) : setSeatingSVG("");
      seatingArrangement.map(item => {
        if (item?.seatArrangement?.name === previousBookingData?.seatingArrangement) {
          handleMenuClick(item?.seatArrangement?.name, `${IMAGES_BASE_URL}${item.seatArrangement.icon}`);
        }
      });
      setPreviousDates();
    } else {
      // Work-specific previous data
      if (previousBookingData?.startingDate && previousBookingData?.endingDate) {
        const startMoment = moment(previousBookingData.startingDate, moment.defaultFormat);
        const endMoment = moment(previousBookingData.endingDate, moment.defaultFormat);
        const formattedStartDate = startMoment.format('YYYY-MM-DD');
        setStartDate(formattedStartDate);

        if (paymentType === WEEKLY) {
          const weeks = Math.ceil(endMoment.diff(startMoment, 'days') / 7);
          setDuration(weeks);
          setCalculatedEndDate(endMoment);
        } else if (paymentType === MONTHLY) {
          const months = endMoment.diff(startMoment, 'months') + 1;
          setDuration(months);
          setCalculatedEndDate(endMoment);
        } else if (paymentType === DAILY) {
          setSelectedDate([getDatesBetween(startMoment.toDate(), endMoment.toDate())]);
        }
      }

      if (blockType === CREATE && selectedDateOnCalender) {
        const selectedMoment = moment(selectedDateOnCalender, moment.defaultFormat);
        const formattedDate = selectedMoment.format('YYYY-MM-DD');
        if (paymentType === DAILY) {
          setSelectedDate([getDatesBetween(selectedMoment.toDate(), selectedMoment.toDate())]);
          setMinSeatForSelectedDate(getDatesBetween(selectedMoment.toDate(), selectedMoment.toDate()), 0);
        } else {
          setStartDate(formattedDate);
        }
      }
    }
  }, [blockType, previousBookingData, selectedDateOnCalender, paymentType]);

  // ===================== MEET: Set Previous Dates =====================
  const setPreviousDates = () => {
    setBookingDate(moment(previousBookingData?.startingDate || moment()).format(DATE_PICKER_DATE_FORMAT));
    if (previousBookingData && previousBookingData.startingDate && previousBookingData.endingDate)
      setSelectedDate([
        getDatesBetween(
          moment(previousBookingData.startingDate, moment.defaultFormat).toDate(),
          moment(previousBookingData.endingDate, moment.defaultFormat).toDate()
        )
      ]);

    if (blockType === CREATE) {
      setSelectedDate([
        getDatesBetween(
          moment(selectedDateOnCalender, moment.defaultFormat).toDate(),
          moment(selectedDateOnCalender, moment.defaultFormat).toDate()
        )
      ]);
      getSlotsForSelectedDate(moment(selectedDateOnCalender).format(DATE_PICKER_DATE_FORMAT));
      setStartDate(moment(selectedDateOnCalender).format(DATE_PICKER_DATE_FORMAT));
      setEndDate(moment(selectedDateOnCalender).format(DATE_PICKER_DATE_FORMAT));
      setStartDateState(moment(selectedDateOnCalender).format(DATE_PICKER_DATE_FORMAT));
    }
  };

  // ===================== MEET: filtered time for initial date =====================
  useEffect(() => {
    if (isMeet) {
      getSlotsForSelectedDate(moment(startDateState).format(DATE_PICKER_DATE_FORMAT));
    }
  }, [availableDates, paymentType]);

  // ===================== WORK: Previous seat count from availability =====================
  useEffect(() => {
    if (!isMeet && previousBookingData?.startingDate && previousBookingData?.endingDate && availableDates) {
      setMinSeatForSelectedDate(
        getDatesBetween(
          moment(previousBookingData.startingDate, moment.defaultFormat).toDate(),
          moment(previousBookingData.endingDate, moment.defaultFormat).toDate()
        ), previousBookingData.guestCount
      );
    }
  }, [availableDates]);

  // ===================== WORK: Weekly/Monthly date range availability =====================
  useEffect(() => {
    if (!isMeet && (paymentType === WEEKLY || paymentType === MONTHLY) && startDate && duration > 0) {
      const availabilityCheck = checkDateRangeAvailability(startDate, duration, paymentType);
      setDateRangeAvailability(availabilityCheck);
      if (!availabilityCheck.available) {
        const unavailableDatesStr = availabilityCheck.unavailableDates.slice(0, 3).join(', ');
        const remainingCount = availabilityCheck.unavailableDates.length - 3;
        const message = `Some dates are unavailable: ${unavailableDatesStr}${remainingCount > 0 ? ` and ${remainingCount} more` : ''}. Please select a different date range.`;
        setUnavailableDatesMessage(message);
        setMaxGuest(0);
      } else {
        setUnavailableDatesMessage('');
        setMaxGuest(availabilityCheck.minGuests);
        if (availabilityCheck.minGuests < space?.events?.[0]?.seatArrangements?.[0]?.max) {
          setUnavailableDatesMessage(`Guest limit is ${availabilityCheck.minGuests} for this ${paymentType.toLowerCase()} period due to availability.`);
        }
      }
    }
  }, [startDate, duration, paymentType, availableDates]);

  // ===================== HELPERS: findAvailableDate =====================
  const findAvailableDate = (date) => {
    return availableDates.find(availableDate => {
      const [selecteAvailableDate] = Object.keys(availableDate);
      if (availableDate[`${moment(date).format(DATE_FORMAT)}T00:00:00.000+0000`] &&
        `${moment(date).format(DATE_FORMAT)}T00:00:00.000+0000` === selecteAvailableDate)
        return availableDate[selecteAvailableDate];
    });
  };

  // ===================== MEET: Block slot helpers =====================
  const setBlocksForSelectedDate = (availableBlockSlots) => {
    const filteredSlotsForBlock = [];
    if (availableBlockSlots && availableBlockSlots.length > 0) {
      availableBlockSlots.forEach(blockSlot => {
        const slotTime = blockSlot.length && blockSlot.split('-');
        slotTime && slotTime.length > 0 && filteredSlotsForBlock.push({
          startTime: moment(slotTime[0], TIME_FORMAT).format(TIME_FORMAT),
          endTime: moment(slotTime[1], TIME_FORMAT).format(TIME_FORMAT),
          name: spaceBlockSlots?.find(item => item.startTime == moment(slotTime[0], TIME_FORMAT).format(TIME_FORMAT))?.name
        });
      });
    }
    return filteredSlotsForBlock;
  };

  const getSlotsForSelectedDate = (date) => {
    if (!date) return;
    const available = findAvailableDate(date);
    if (available && available[`${moment(date).format(DATE_FORMAT)}T00:00:00.000+0000`]) {
      if (blockType === EDIT && paymentType === HOURLY) {
        if (available[`${moment(date).format(DATE_FORMAT)}T00:00:00.000+0000`]?.status && available[`${moment(date).format(DATE_FORMAT)}T00:00:00.000+0000`].slots) {
          setAvailableSlots(editReservationMergeSlot({ startTime: previousBookingData?.startTime, endTime: previousBookingData?.endTime }, available[`${moment(date).format(DATE_FORMAT)}T00:00:00.000+0000`].slots));
        } else {
          setAvailableSlots([{ startTime: previousBookingData?.startTime, endTime: previousBookingData?.endTime }]);
        }
      } else {
        setAvailableSlots(available[`${moment(date).format(DATE_FORMAT)}T00:00:00.000+0000`].slots);
      }

      if (paymentType === BLOCK) {
        const availableBlockSlots = available[`${moment(date).format(DATE_FORMAT)}T00:00:00.000+0000`]?.slots || [];
        const filteredSlotsForBlock = setBlocksForSelectedDate(availableBlockSlots);

        if (blockType === EDIT && moment(previousBookingData.startingDate, DATE_FORMAT).isSame(moment(date, DATE_PICKER_DATE_FORMAT))) {
          const previousSelectedSlots = previousBookingData?.blocks;
          if (Array.isArray(previousSelectedSlots)) {
            setSelectedChips([...previousSelectedSlots]);
            if (filteredSlotsForBlock.length > 0) {
              const merged = [
                ...filteredSlotsForBlock,
                ...previousSelectedSlots.filter(
                  (ps) => !filteredSlotsForBlock.some((fs) => fs.startTime === ps.startTime && fs.endTime === ps.endTime)
                ),
              ];
              setSlots(merged);
            } else {
              setSlots((prev) => [...prev]);
            }
          }
        } else {
          availableBlockSlots.length > 0 && setSlots(filteredSlotsForBlock);
        }
      }
    }
  };

  // ===================== MEET: Seating handler =====================
  const handleMenuClick = (name, svg) => {
    setSeatingSVG && setSeatingSVG(svg);
    setSeatingText && setSeatingText(name);
    setMaxGuest(seatingArrangement[0]?.seatArrangement && seatingArrangement.filter(item => {
      if (item.seatArrangement.name === name) return item;
    })[0]?.min);
    if (blockType === CREATE) setViewGuestCount(1);
  };

  // ===================== MEET: setBookingDate =====================
  const setBookingDate = date => {
    getSlotsForSelectedDate(date);
    setStartDate(moment(date).format(DATE_PICKER_DATE_FORMAT));
    setEndDate(moment(date).format(DATE_PICKER_DATE_FORMAT));
    setStartDateState(date);
    setSelectedDate([date]);
  };

  // ===================== WORK: Guest seat helpers =====================
  const setMinSeatForSelectedDate = (dateRange, previousBookingGuestCount = 0) => {
    const availableSeatCountArray = [];
    dateRange.forEach(selDate => {
      const available = findAvailableDate(selDate);
      if (available && available[`${moment(selDate).format(DATE_FORMAT)}T00:00:00.000+0000`]) {
        available[`${moment(selDate).format(DATE_FORMAT)}T00:00:00.000+0000`]?.status &&
          availableSeatCountArray.push(available[`${moment(selDate).format(DATE_FORMAT)}T00:00:00.000+0000`].availableSeats);
      }
    });
    const minSeatCount = availableSeatCountArray.length ? (Math.min.apply(Math, availableSeatCountArray) + previousBookingGuestCount) : previousBookingGuestCount;
    if (availableDates.length > 0) setGuestCountErrorMessage(viewGuestCount > minSeatCount);
    setMaxGuest(minSeatCount);
  };

  const setAvailableGuestCount = () => {
    (selectedDate.length && selectedDate[0]?.constructor === Array) && (setMinSeatForSelectedDate(selectedDate[0], getPreviousGuestCount(selectedDate[0])));
  };

  const getPreviousGuestCount = (selectedDate) => {
    if (blockType === EDIT && previousBookingData?.startingDate === `${moment(selectedDate[0]).format(DATE_FORMAT)}T00:00:00.000+0000` && previousBookingData?.endingDate === `${moment(selectedDate[selectedDate.length - 1]).format(DATE_FORMAT)}T00:00:00.000+0000`) {
      return previousBookingData.guestCount;
    }
    return 0;
  };

  const handleRangeSelectedDate = (date) => {
    setSelectedDate(date);
    if (!isMeet) setAvailableGuestCount();
  };

  // ===================== WORK: Date range availability check =====================
  const checkDateRangeAvailability = (startDate, duration, paymentType) => {
    if (paymentType === DAILY) return { available: true, minGuests: maxGuest };
    const dateRange = [];
    const unavailableDates = [];
    const availableGuestCounts = [];
    for (let i = 0; i < duration; i++) {
      if (paymentType === WEEKLY) {
        for (let week = 0; week < duration; week++) {
          for (let day = 0; day < 7; day++) {
            dateRange.push(moment(startDate).add(week, 'weeks').add(day, 'days').format('YYYY-MM-DD'));
          }
        }
        break;
      } else if (paymentType === MONTHLY) {
        const monthStart = moment(startDate).add(i, 'months');
        const daysInMonth = monthStart.clone().endOf('month').date();
        for (let day = 0; day < daysInMonth; day++) {
          dateRange.push(monthStart.clone().add(day, 'days').format('YYYY-MM-DD'));
        }
      }
    }
    const uniqueDates = [...new Set(dateRange)];
    uniqueDates.forEach(date => {
      const available = findAvailableDate(date);
      const dateKey = `${moment(date).format(DATE_FORMAT)}T00:00:00.000+0000`;
      if (!available || !available[dateKey] || !available[dateKey].status) {
        unavailableDates.push(moment(date).format('MMM DD, YYYY'));
      } else {
        availableGuestCounts.push(available[dateKey].availableSeats);
      }
    });
    const minGuestCount = availableGuestCounts.length > 0 ? Math.min(...availableGuestCounts) : 0;
    return { available: unavailableDates.length === 0, unavailableDates, minGuests: minGuestCount, totalDatesChecked: uniqueDates.length };
  };

  // ===================== GUEST CHANGE =====================
  const handleGuestChange = value => {
    if (!isMeet) setGuestCountErrorMessage(false);
    const maxAllowed = !isMeet && !dateRangeAvailability.available ? 0 : maxGuest;
    if (value > 0 && value <= maxAllowed) {
      setViewGuestCount(value);
    } else if (value > maxAllowed && maxAllowed > 0) {
      setViewGuestCount(maxAllowed);
    }
  };

  // ===================== WORK: Duration change =====================
  const handleDurationChange = (value) => {
    let maxDuration = paymentType === MONTHLY ? 12 : paymentType === WEEKLY ? 52 : 10;
    if (value >= 0 && value <= maxDuration) {
      setDuration(value);
      if (value > 0 && startDate) {
        if (paymentType === WEEKLY) {
          setCalculatedEndDate(moment(startDate).add(value, 'weeks').subtract(1, 'days'));
        } else if (paymentType === MONTHLY) {
          setCalculatedEndDate(moment(startDate).add(value, 'months').subtract(1, 'days'));
        }
      } else {
        setCalculatedEndDate(null);
      }
    }
  };

  // ===================== WORK: Single date change (weekly/monthly) =====================
  const handleSingleDateChange = (date) => {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    setStartDate(formattedDate);
    if (duration > 0) {
      if (paymentType === WEEKLY) setCalculatedEndDate(moment(date).add(duration, 'weeks').subtract(1, 'days'));
      else if (paymentType === MONTHLY) setCalculatedEndDate(moment(date).add(duration, 'months').subtract(1, 'days'));
    }
  };

  // ===================== PAYMENT TYPE CHANGE =====================
  const handlePaymentTypeChange = (newPaymentType) => {
    setPaymentType(newPaymentType);
    if (isMeet) {
      // Reset meet-specific state when switching charging types
      setSlots([]);
      setSelectedChips([]);
      setAvailableSlots(false);
    }
    if (!isMeet) {
      if (blockType !== EDIT || newPaymentType !== (previousBookingData?.chargingType || selectedSpaceData?.chargingType)) {
        setDuration(1);
        setCalculatedEndDate(null);
        setGuestCountErrorMessage(false);
        setUnavailableDatesMessage('');
        setDateRangeAvailability({ available: true, minGuests: 0 });
        if (newPaymentType === DAILY) {
          setDatePickers([`${moment().format('L')} - ${moment().format('L')}`]);
        } else {
          setDatePickers([]);
          setStartDate(moment().format('YYYY-MM-DD'));
        }
      }
    }
  };

  const getFilteredChargingTypes = () => {
    if (!isMeet && blockType === EDIT) {
      return chargingTypes?.filter(ct => ct.chargingType.name === selectedSpaceData.chargingType) || [];
    }
    return chargingTypes;
  };

  // ===================== MEET: Chip handlers =====================
  const chipColor = slot => selectedChips.includes(slot) && 'selected-chip';

  const handleTimeChipClick = slot => {
    if (selectedChips.includes(slot)) {
      setSelectedChips(selectedChips.filter(v => v !== slot));
    } else {
      setSelectedChips([...selectedChips, slot]);
    }
  };

  // ===================== DATE CHANGE (Range) =====================
  const handleDateChange = (dateIndex, date) => {
    const filteredList = datePickers.map((other, index) => index === dateIndex ? date : other);
    setDatePickers(filteredList);
    const dates = [].concat(...selectedDate);
    setRange(dates.length);
  };

  // ===================== FORM HANDLERS =====================
  const handleBookingDetailsOnChange = useCallback((name, value) => {
    setBookingDetails(b => ({ ...b, [name]: value }));
  }, []);

  const handleGuestDetailsOnChange = useCallback((name, value) => {
    setGuestDetails(b => ({ ...b, [name]: value }));
  }, []);

  const getDateClassName = date => {
    const calendarDate = moment(date).format('L');
    const today = moment().format('L');
    return calendarDate === today ? 'current-date' : null;
  };

  const handleBack = () => setVisible(false);

  const validateEmail = (email) => /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,10})+$/.test(email);

  // ===================== BUTTON VALIDATION =====================
  useEffect(() => {
    if (!bookingDetails.title) {
      setButtonDisplayStatus(true);
    } else if (isMeet && paymentType === BLOCK && selectedChips.length === 0) {
      setButtonDisplayStatus(true);
    } else if (isMeet && !findAvailableDate(startDate)?.[`${moment(startDate).format(DATE_FORMAT)}T00:00:00.000+0000`]?.status && blockType === CREATE && paymentType !== DAILY) {
      setButtonDisplayStatus(true);
    } else if (!isMeet && (paymentType === WEEKLY || paymentType === MONTHLY) && !dateRangeAvailability.available) {
      setButtonDisplayStatus(true);
    } else if (!isMeet && (paymentType === WEEKLY || paymentType === MONTHLY) && duration < 1) {
      setButtonDisplayStatus(true);
    } else if (guestDetails?.email?.length > 0 && !validateEmail(guestDetails.email)) {
      setButtonDisplayStatus(true);
    } else {
      setButtonDisplayStatus(false);
    }
  }, [bookingDetails.title, seatingText, selectedChips, availableDates, startDate, guestDetails, dateRangeAvailability, paymentType, duration]);

  // ===================== ON PROCEED =====================
  const handleOnProceed = () => {
    if (isMeet) {
      handleMeetProceed();
    } else {
      handleWorkProceed();
    }
  };

  const handleMeetProceed = () => {
    const dates = [].concat(...selectedDate);
    let chargingType;
    let payload = {};
    const selectedSeating = seatingArrangement.find(seating => seating.seatArrangement?.name === seatingText)?.seatArrangement || '';
    let submitStartDate = startDate;
    let submitEndDate = endDate;

    switch (paymentType) {
      case DAILY:
        submitStartDate = dates[0];
        submitEndDate = dates[dates.length - 1];
        chargingType = events?.[0]?.chargings?.find(item => item.chargingType.name === DAILY);
        break;
      case HOURLY:
        submitEndDate = dates[0];
        submitStartDate = dates[0];
        chargingType = events?.[0]?.chargings?.find(item => item.chargingType.name === HOURLY);
        break;
      default:
        submitEndDate = startDate;
        break;
    }

    if (paymentType === BLOCK) {
      const selectedBlocks = selectedChips.map(chip => ({
        name: chip.name, startTime: chip.startTime, endTime: chip.endTime
      }));
      payload = {
        id: selectedSpaceData?.id,
        space: space.id,
        event: {
          eventType: space.events[0]?.eventType?.name,
          eventTypeId: space.events[0]?.eventType?.id,
          guestCount: viewGuestCount,
          startDate: startDate ? moment(startDate, DATE_PICKER_DATE_FORMAT).format(CALENDER_DATE_FORMAT) : '',
          endDate: endDate ? moment(startDate, DATE_PICKER_DATE_FORMAT).format(CALENDER_DATE_FORMAT) : '',
          chargingTypeId: chargingTypes.find(item => item.chargingType.name === paymentType).chargingType.id,
          seatArrangement: { name: selectedSeating?.name, description: selectedSeating?.description, icon: selectedSeating?.icon },
          blocks: selectedBlocks,
        },
        details: { title: bookingDetails.title, description: bookingDetails.description },
        guest: { firstName: guestDetails.firstName, lastName: guestDetails.lastName, contactNumber: guestDetails.contact, email: guestDetails.email }
      };
    } else if (paymentType === HOURLY) {
      payload = {
        id: selectedSpaceData?.id,
        space: space.id,
        event: {
          eventType: space.events?.[0]?.eventType?.name || "",
          eventTypeId: space.events?.[0]?.eventType?.id || "",
          guestCount: viewGuestCount,
          startDate: moment(submitStartDate, DATE_PICKER_DATE_FORMAT).format(CALENDER_DATE_FORMAT) || moment(startDate, DATE_PICKER_DATE_FORMAT).format(CALENDER_DATE_FORMAT),
          endDate: moment(submitEndDate, DATE_PICKER_DATE_FORMAT).format(CALENDER_DATE_FORMAT) || moment(endDate, DATE_PICKER_DATE_FORMAT).format(CALENDER_DATE_FORMAT),
          chargingTypeId: chargingTypes?.find(item => item?.chargingType?.name === paymentType)?.chargingType?.id,
          seatArrangement: { name: selectedSeating?.name, description: selectedSeating?.description, icon: selectedSeating?.icon },
          blocks: [{ name: chargingType?.pricingOptions?.[0].name, startTime: timePickers?.[0]?.beginTime, endTime: timePickers?.[0]?.endTime }],
        },
        details: { title: bookingDetails.title, description: bookingDetails.description },
        guest: { firstName: guestDetails.firstName, lastName: guestDetails.lastName, contactNumber: guestDetails.contact, email: guestDetails.email }
      };
    } else {
      payload = {
        id: selectedSpaceData?.id,
        space: space.id,
        event: {
          eventType: space.events[0]?.eventType?.name,
          eventTypeId: space.events[0]?.eventType?.id,
          guestCount: viewGuestCount,
          startDate: moment(submitStartDate, DATE_PICKER_DATE_FORMAT).format(CALENDER_DATE_FORMAT),
          endDate: moment(submitEndDate, DATE_PICKER_DATE_FORMAT).format(CALENDER_DATE_FORMAT),
          chargingTypeId: chargingTypes.find(item => item.chargingType.name === paymentType).chargingType.id,
          blocks: [{
            name: chargingTypes?.find(ct => ct.chargingType.name === paymentType).pricingOptions?.[0]?.name || DAILY,
            startTime: chargingTypes?.find(ct => ct.chargingType.name === paymentType).pricingOptions?.[0]?.startTime || "",
            endTime: chargingTypes?.find(ct => ct.chargingType.name === paymentType).pricingOptions?.[0]?.endTime || ''
          }],
          seatArrangement: {
            name: selectedSeating ? selectedSeating?.name : '',
            description: selectedSeating ? selectedSeating?.description : '',
            icon: selectedSeating ? selectedSeating?.icon : ''
          }
        },
        details: { title: bookingDetails.title, description: bookingDetails.description },
        guest: { firstName: guestDetails.firstName, lastName: guestDetails.lastName, contactNumber: guestDetails.contact, email: guestDetails.email }
      };
    }
    onProceed(payload);
    resetForm();
  };

  const handleWorkProceed = () => {
    const selectedChargingType = chargingTypes?.find(ct => ct.chargingType.name === paymentType);
    let startDateForPayload, endDateForPayload;

    if (paymentType === DAILY) {
      startDateForPayload = datePickers.length ? moment(datePickers[0].split('-')[0].trim(), 'MM/DD/YYYY').format(CALENDER_DATE_FORMAT) : '';
      endDateForPayload = datePickers.length ? moment(datePickers[0].split('-')[1].trim(), 'MM/DD/YYYY').format(CALENDER_DATE_FORMAT) : '';
    } else if (paymentType === WEEKLY) {
      startDateForPayload = moment(startDate).format(CALENDER_DATE_FORMAT);
      endDateForPayload = calculatedEndDate ? moment(calculatedEndDate).format(CALENDER_DATE_FORMAT) : moment(startDate).add(duration, 'weeks').subtract(1, 'days').format(CALENDER_DATE_FORMAT);
    } else if (paymentType === MONTHLY) {
      startDateForPayload = moment(startDate).format(CALENDER_DATE_FORMAT);
      endDateForPayload = calculatedEndDate ? moment(calculatedEndDate).format(CALENDER_DATE_FORMAT) : moment(startDate).add(duration, 'months').subtract(1, 'days').format(CALENDER_DATE_FORMAT);
    } else {
      startDateForPayload = moment(startDate).format(CALENDER_DATE_FORMAT);
      endDateForPayload = moment(startDate).format(CALENDER_DATE_FORMAT);
    }

    const payload = {
      id: selectedSpaceData?.id,
      space: space.id,
      event: {
        eventType: events[0]?.eventType?.name,
        eventTypeId: events[0]?.eventType?.id,
        guestCount: viewGuestCount,
        startDate: startDateForPayload,
        endDate: endDateForPayload,
        chargingTypeId: selectedChargingType?.chargingType?.id,
        duration: duration,
        blocks: [{
          name: selectedChargingType?.pricingOptions?.[0]?.name || paymentType,
          startTime: selectedChargingType?.pricingOptions?.[0]?.startTime || null,
          endTime: selectedChargingType?.pricingOptions?.[0]?.endTime || null
        }],
      },
      details: { title: bookingDetails.title, description: bookingDetails.description },
      guest: { firstName: guestDetails.firstName, lastName: guestDetails.lastName, contactNumber: guestDetails.contact, email: guestDetails.email }
    };
    onProceed(payload);
    resetForm();
  };

  const resetForm = () => {
    setGuestDetails({ firstName: '', lastName: '', contact: '', email: '' });
    setBookingDetails({ title: '', description: '' });
    if (isMeet) {
      setSeatingText();
      setSeatingSVG();
      setTimePickers([{ beginTime: '3:00 am', endTime: '5:00 pm' }]);
    }
  };

  // ===================== Determine if we have loading data =====================
  const hasAvailabilityData = isMeet ? availableDates.length > 0 : availableDates.length > 0;

  // ===================== Should show bottom form =====================
  const showBottomForm = isMeet
    ? ((timePickers[0].beginTime && timePickers[0].endTime) || selectedChips.length)
    : (paymentType === DAILY ? selectedDate : startDate);

  // ===================== RENDER =====================
  return (
    <div className="create-reservation-modal">
      <div className="crm-fixed-header">
        <img src={arrow} alt="arrow" className="crm-arrow-img" onClick={() => handleBack()} />
        <span className="crm-header-text">{space.spaceName}</span>
      </div>
      <div className="crm-content">
        <div className="crm-header-row">
          <span className="crm-title">{blockType} Reservation</span>
          <Icon type="close" className="crm-close-icon" onClick={() => handleBack()} />
        </div>

        {/* ========== ROW: Charging Type + Date ========== */}
        <div className="crm-form-row">
          <div className="crm-form-col crm-form-col--half">
            <Select
              className="crm-select"
              onChange={value => handlePaymentTypeChange(value)}
              defaultValue={blockType !== EDIT ? chargingTypes.length ? chargingTypes[0].chargingType.name : 'Daily' : selectedSpaceData.chargingType || 'Daily'}
              value={paymentType}
              style={{ width: '100%' }}
            >
              {getFilteredChargingTypes()?.map(type => (
                <Select.Option key={type?.chargingType?.id || type?.chargingType?.name} value={type?.chargingType?.name}>{type?.chargingType?.name}</Select.Option>
              ))}
            </Select>
          </div>
          <div className="crm-form-col crm-form-col--half crm-date-wrapper">
            {hasAvailabilityData ? (
              <div className="date-block-wrapper">
                {/* MEET: Non-daily = SingleDatePicker, Daily = RangePicker */}
                {/* WORK: Daily = RangePicker, else = SingleDatePicker */}
                {isMeet ? (
                  paymentType !== DAILY ? (
                    <div className="minus-btn-wrapper">
                      <SingleDatePicker
                        selectedDate={startDateState}
                        getDateClassName={getDateClassName}
                        label={'Date'}
                        minDate={dayBeforeToday}
                        onChange={setBookingDate}
                        availableDates={availableDates}
                        value={startDate}
                        blockType={blockType}
                        setPreviousDates={setPreviousDates}
                        urlUpdate={false}
                      />
                    </div>
                  ) : (
                    datePickers.map((picker, index) => (
                      <div className="minus-btn-wrapper" key={index}>
                        <RangePicker
                          className="date-select"
                          key={index}
                          index={index}
                          setDuration={setDuration}
                          setSelectedDate={setSelectedDate}
                          selectedDate={selectedDate}
                          handleDateChange={handleDateChange}
                          dateString={picker}
                          availableDates={availableDates}
                          formFrom={'EDIT_RESERVATION'}
                          blockType={blockType}
                          setPreviousDates={setPreviousDates}
                          urlUpdate={false}
                        />
                      </div>
                    ))
                  )
                ) : (
                  paymentType === DAILY ? (
                    datePickers.map((picker, index) => (
                      <div className="minus-btn-wrapper" key={index}>
                        <RangePicker
                          className="date-select"
                          key={index}
                          index={index}
                          setDuration={setDuration}
                          setSelectedDate={handleRangeSelectedDate}
                          selectedDate={selectedDate}
                          handleDateChange={handleDateChange}
                          dateString={picker}
                          availableDates={availableDates}
                          formFrom={'EDIT_RESERVATION'}
                          blockType={blockType}
                          urlUpdate={false}
                        />
                      </div>
                    ))
                  ) : (
                    <div>
                      <SingleDatePicker
                        selectedDate={startDate}
                        label="Start Date"
                        minDate={moment().toDate()}
                        onChange={handleSingleDateChange}
                        availableDates={availableDates}
                        value={startDate}
                        blockType={blockType}
                        urlUpdate={false}
                      />
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="crm-loader"><ReservationLoader /></div>
            )}
          </div>
        </div>

        {/* ========== MEET: Time Picker Row (Hourly) ========== */}
        {isMeet && paymentType === HOURLY &&
          timePickers.map((picker, index) => (
            <div className="crm-form-row crm-time-wrapper" key={index}>
              <TimeRangePicker
                disable={!(availableSlots?.length > 0)}
                key={index}
                startLimit={previousBookingData?.startTime || '7:00 am'}
                endLimit={previousBookingData?.endTime || '8:00 am'}
                timeRangeType={blockType}
                pickers={timePickers}
                setTimePicker={setTimePickers}
                index={index}
                availableSlots={availableSlots}
                acceptableBookingTime={acceptableBookingTime}
                minimumBookingPeriod={minimumBookingPeriod}
                useAntd={true}
              />
            </div>
          ))
        }

        {/* ========== MEET: Date not available ========== */}
        {isMeet && !(availableSlots?.length > 0) && !(availableDates?.length > 0) && !(isFetchingAvailableDates) &&
          <p className="crm-date-unavailable">Date is not available.</p>
        }

        {/* ========== WORK: Duration for Weekly/Monthly ========== */}
        {!isMeet && hasAvailabilityData && (paymentType === WEEKLY || paymentType === MONTHLY) && (
          <div className="crm-form-row">
            <div className="crm-form-col crm-form-col--half">
              <div className={`crm-float-label ${duration ? 'has-value' : ''}`}>
                <InputNumber
                  className="crm-input-number"
                  min={1}
                  max={paymentType === MONTHLY ? 12 : 52}
                  placeholder={paymentType === WEEKLY ? '# of Weeks' : '# of Months'}
                  onChange={(value) => handleDurationChange(value)}
                  value={duration}
                />
                <label className="crm-label">{paymentType === WEEKLY ? '# of Weeks' : '# of Months'}</label>
              </div>
            </div>
          </div>
        )}

        {/* ========== WORK: Reservation period display ========== */}
        {!isMeet && (paymentType === WEEKLY || paymentType === MONTHLY) && calculatedEndDate && (
          <div className="crm-date-range-display">
            <strong>Reservation period:</strong> {moment(startDate).format('MMM DD, YYYY')} - {moment(calculatedEndDate).format('MMM DD, YYYY')}
          </div>
        )}

        {/* ========== Number of guests ========== */}
        <div className="crm-form-row">
          <div className="crm-form-col crm-form-col--half">
            <div className={`crm-float-label ${viewGuestCount ? 'has-value' : ''}`}>
              <InputNumber
                className="crm-input-number"
                min={1}
                placeholder="Number of guests"
                onChange={(value) => handleGuestChange(value)}
                value={viewGuestCount}
              />
              <label className="crm-label">Number of guests</label>
            </div>
          </div>
        </div>

        {/* ========== WORK: Error messages ========== */}
        {!isMeet && hasAvailabilityData && (
          <>
            {guestCountErrorMessage && <span className="crm-date-unavailable"> Sorry! Max availability is {maxGuest} for the selected date(s) </span>}
            {unavailableDatesMessage && (
              <div className={`crm-availability-msg ${dateRangeAvailability.available ? 'crm-availability-msg--info' : 'crm-availability-msg--error'}`}>
                <span className="crm-msg-text">{unavailableDatesMessage}</span>
              </div>
            )}
          </>
        )}

        {/* ========== MEET: Seating Arrangement ========== */}
        {isMeet && seatingArrangement?.length > 1 && (
          <div className="crm-seating-wrapper">
            {seatingText && seatingText !== 'Seating Arrangement' && <label className="crm-label">Seating Arrangement</label>}
            <SeatingArrangementSelect
              seatingArrangement={seatingArrangement}
              handleMenuClick={handleMenuClick}
              seatingSVG={seatingSVG}
              seatingText={seatingText}
              label={seatingText || "Seating Arrangement"}
            />
          </div>
        )}

        {/* ========== MEET: Block Chips ========== */}
        {isMeet && paymentType === BLOCK && (
          <div className="crm-chips-wrapper">
            <div className="crm-chips-label">Available blocks</div>
            <div className="crm-chips-inner">
              {slots?.map((slot, index) =>
                <Chip key={index} className={chipColor(slot)} onClick={() => handleTimeChipClick(slot, index)} label={`${slot.startTime} - ${slot.endTime}`} />
              )}
            </div>
          </div>
        )}
      </div>

      {/* ========== BOTTOM FORM: Booking & Guest Details ========== */}
      {showBottomForm && (
        <>
          <div className="crm-content crm-content--details">
            <hr className="crm-divider" />
            <p className="crm-section-title">Reservation Details</p>
            <div className="crm-form-row">
              <div className="crm-form-col crm-form-col--full">
                <div className={`crm-float-label ${bookingDetails.title ? 'has-value' : ''}`}>
                  <Input
                    className="crm-input"
                    placeholder="Title *"
                    onChange={e => handleBookingDetailsOnChange('title', e.target.value)}
                    value={bookingDetails.title}
                  />
                  <label className="crm-label">Title</label>
                </div>
              </div>
            </div>
            <div className="crm-form-row">
              <div className="crm-form-col crm-form-col--full">
                <div className={`crm-float-label ${bookingDetails.description ? 'has-value' : ''}`}>
                  <Input.TextArea
                    className="crm-textarea"
                    placeholder="Description"
                    rows={3}
                    onChange={e => handleBookingDetailsOnChange('description', e.target.value)}
                    value={bookingDetails.description}
                  />
                  <label className="crm-label">Description</label>
                </div>
              </div>
            </div>

            <hr className="crm-divider" />
            <p className="crm-section-title">Guest Personal Details</p>
            <div className="crm-form-row">
              <div className="crm-form-col crm-form-col--half">
                <div className={`crm-float-label ${guestDetails.firstName ? 'has-value' : ''}`}>
                  <Input
                    className="crm-input"
                    placeholder="First Name"
                    onChange={e => handleGuestDetailsOnChange('firstName', e.target.value)}
                    value={guestDetails.firstName}
                  />
                  <label className="crm-label">First Name</label>
                </div>
              </div>
              <div className="crm-form-col crm-form-col--half">
                <div className={`crm-float-label ${guestDetails.lastName ? 'has-value' : ''}`}>
                  <Input
                    className="crm-input"
                    placeholder="Last Name"
                    onChange={e => handleGuestDetailsOnChange('lastName', e.target.value)}
                    value={guestDetails.lastName}
                  />
                  <label className="crm-label">Last Name</label>
                </div>
              </div>
            </div>
            <div className="crm-form-row">
              <div className="crm-form-col crm-form-col--full">
                <div className={`crm-float-label ${guestDetails.contact ? 'has-value' : ''}`}>
                  <Input
                    className="crm-input"
                    placeholder="Contact Number"
                    onChange={e => handleGuestDetailsOnChange('contact', e.target.value)}
                    value={guestDetails.contact}
                  />
                  <label className="crm-label">Contact Number</label>
                </div>
              </div>
            </div>
            <div className="crm-form-row">
              <div className="crm-form-col crm-form-col--full">
                <div className={`crm-float-label ${guestDetails.email ? 'has-value' : ''}`}>
                  <Input
                    className="crm-input"
                    placeholder="Email"
                    type="email"
                    onChange={e => handleGuestDetailsOnChange('email', e.target.value)}
                    value={guestDetails.email}
                  />
                  <label className="crm-label">Email</label>
                </div>
              </div>
            </div>
          </div>
          <div className="crm-footer">
            <button
              className="crm-submit-btn"
              disabled={buttonDisplayStatus || (!isMeet && paymentType !== DAILY && duration < 1)}
              onClick={handleOnProceed}
            >
              {blockType} Reservation
            </button>
          </div>
        </>
      )}
    </div>
  );
});

export default CreateReservationModal;
